import { useCallback, useEffect, useState } from "react";
import { Loader2, Send, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { mutate, setSimulateOffline, getSimulateOffline } from "@/offline/apiClient";
import { drainQueue } from "@/offline/sync";
import { useSyncStatus, statusLabel } from "@/offline/useSyncStatus";

export default function DemoPing() {
  const [msg, setMsg] = useState("Ping from field device");
  const [busy, setBusy] = useState(false);
  const [simulated, setSimulated] = useState(getSimulateOffline());
  const [logs, setLogs] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const s = useSyncStatus();
  const label = statusLabel(s);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/system/ping-log", { params: { page: 1, page_size: 20 } });
      setLogs(data.items);
    } catch (_) { /* offline */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [refresh]);

  const submit = async () => {
    setBusy(true);
    try {
      const gps = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
          () => resolve(null),
          { timeout: 3000 }
        );
        setTimeout(() => resolve(null), 3200);
      });
      const res = await mutate({
        method: "POST",
        path: "/system/ping-log",
        body: { message: msg, gps, client_ts: new Date().toISOString() },
        entity_type: "system_ping",
      });
      setLastResult(res);
      if (res.immediate) {
        toast.success("Sent immediately");
      } else {
        toast(`Queued — key ${res.idempotency_key.slice(0, 8)}`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="demo-ping">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-neutral-400">Demo · Offline plumbing</div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-900">
          Ping-log
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Prove the offline-queue end-to-end. Toggle "Simulate offline", submit pings, then toggle back — they replay automatically.
          Current status: <span className="font-semibold text-neutral-700">{label.text}</span>.
        </p>
      </header>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs">
              <WifiOff className="h-3.5 w-3.5 text-neutral-500" />
              <span className="text-neutral-500">Simulate offline</span>
              <Switch
                data-testid="demo-sim-offline"
                checked={simulated}
                onCheckedChange={(v) => { setSimulateOffline(v); setSimulated(v); if (!v) drainQueue().catch(() => {}); }}
              />
            </div>
            {lastResult && (
              <div className="text-[11px] text-neutral-500">
                Last: {lastResult.immediate ? "sent" : "queued"} · key {lastResult.idempotency_key.slice(0, 8)}
              </div>
            )}
          </div>
          <Textarea
            data-testid="demo-ping-message"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={2}
            className="resize-none"
          />
          <Button
            onClick={submit}
            disabled={busy || !msg.trim()}
            className="tulsi-gradient text-white font-semibold h-11 w-full sm:w-auto"
            data-testid="demo-ping-send"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send ping
          </Button>
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your ping history ({logs.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 && (
            <div className="text-center text-sm text-neutral-500 py-6" data-testid="demo-ping-empty">
              No pings yet.
            </div>
          )}
          {logs.length > 0 && (
            <div className="divide-y divide-neutral-100">
              {logs.map((p) => (
                <div key={p.id} className="px-5 py-3 text-sm flex items-start justify-between gap-3" data-testid={`ping-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-neutral-800 truncate">{p.message}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {new Date(p.server_ts).toLocaleString()} · id {p.id.slice(0, 8)} · key {p.idempotency_key?.slice(0, 8)}
                    </div>
                  </div>
                  {p.gps && (
                    <div className="text-[11px] text-neutral-500 shrink-0 whitespace-nowrap">
                      {p.gps.lat?.toFixed(3)}, {p.gps.lng?.toFixed(3)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
